import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Checkbox, Divider, FormControlLabel, TextField, Typography, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import Tiptap from 'components/Tiptap/Tiptap'; // TipTap 컴포넌트

import SnackbarAlert from 'components/SnackbarAlert';
import { useSnackbarStore } from 'store/useSnackbarStore';
import { useNavigate, useParams } from 'react-router-dom';
import { getNoticesIdAPI, putNoticesUpdateIdAPI } from 'apis/notice/noticeAPI';
import { useTranslation } from 'react-i18next';

export default function NoticeEdit() {
  const { id } = useParams<{ id: string }>(); // URL에서 noticeId 가져오기
  const [title, setTitle] = useState('');
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [isMainPage, setIsMainPage] = useState(false);
  const [isFixed, setIsFixed] = useState(false);
  const [content, setContent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const editorRef = useRef<any>(null);
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  const navigate = useNavigate();

  const fetchNoticeDetails = async () => {
    try {
      const data = await getNoticesIdAPI(Number(id)); // API 호출
      if (data) {
        setTitle(data.data.title);
        setEndDate(
          data.data.popupExpiredAt && dayjs(data.data.popupExpiredAt).isBefore(dayjs())
            ? dayjs() // 오늘 날짜로 설정
            : data.data.popupExpiredAt
              ? dayjs(data.data.popupExpiredAt) // 유효한 종료 날짜 설정
              : null // 종료 날짜 없으면 null
        );
        setIsMainPage(data.data.isPopup);
        setIsFixed(data.data.isFixed);
        setContent(data.data.content);
      }
    } catch (error) {
      showSnackbar('공지사항을 불러오는 중 오류가 발생했습니다.', 'error');
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    console.log('noticeId', id);
    fetchNoticeDetails();
  }, [id]);

  // 저장 버튼 핸들러
  const handleSubmit = async () => {
    if (!title.trim()) {
      showSnackbar(t('notice.new.warn_title'), 'warning');
      return;
    }
    if (isMainPage && !endDate) {
      showSnackbar(t('notice.new.warn_date'), 'warning');
      return;
    }

    const contentHTML = editorRef?.current?.getHTML() || '';
    setContent(contentHTML);

    const requestData = {
      title,
      content: contentHTML,
      isFixed,
      isHidden: false,
      isPopup: isMainPage,
      popupExpiredAt: isMainPage ? dayjs(endDate).format('YYYY-MM-DD') : null
    };

    try {
      await putNoticesUpdateIdAPI(Number(id), requestData); // 수정 API 호출
      showSnackbar(t('notice.edit.success_save'), 'success');
      navigate('/notice');
    } catch (error) {
      showSnackbar(t('notice.edit.error_save'), 'error');
    }
  };

  return (
    <Box
      padding={2}
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{
        width: { xs: '100vw', sm: '100%' },
        justifyContent: 'center'
      }}
    >
      {isNaN(Number(id)) ? (
        <>
          <Box>공지사항이 없습니다</Box>
        </>
      ) : (
        <>
          <SnackbarAlert />
          <Box borderRadius={1} display="flex" flexDirection="column" gap={1}>
            <Typography variant="h5" fontWeight="bold">
              {t('notice.edit.title')}
            </Typography>

            <Divider flexItem />
          </Box>

          <Box padding={2} paddingBottom={2} borderRadius={1} bgcolor={theme.palette.contentBlock.background} display="flex" flexDirection="column" gap={2}>
            <Box display="flex" flexDirection="column" gap={1} padding={2} borderRadius={1} boxShadow={1} bgcolor={theme.palette.contentBlock.main}>
              {/* 제목 */}
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1}>
                <Typography sx={{ width: { xs: '100%', sm: '90px' } }} variant="caption" height={32} lineHeight={2.75}>
                  {t('notice.edit.table.title')}
                </Typography>
                <TextField
                  size="small"
                  placeholder={t('notice.edit.table.title_placeholder')}
                  value={title} onChange={(e) => setTitle(e.target.value)} sx={{ flexGrow: 1 }} />
              </Box>

              {/* 상단고정 */}
              <Box display="flex" flexDirection="row" alignItems="center">
                <Typography width={90} variant="caption" height={32} lineHeight={2.75}>
                  {t('notice.edit.table.fixed')}
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked={isFixed} onChange={() => setIsFixed(!isFixed)} />}
                  label={t('notice.edit.table.fixed_checkbox')}
                  sx={{
                    transform: 'scale(0.8)', // 크기 축소
                    padding: '2px' // 내부 패딩 조정
                  }}
                />
              </Box>

              {/* 팝업 설정 */}
              <Box display="flex" flexDirection="row" alignItems="center">
                <Typography width={90} variant="caption" height={32} lineHeight={2.75}>
                  {t('notice.edit.table.popup')}
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked={isMainPage} onChange={() => setIsMainPage(!isMainPage)} />}
                  label={t('notice.edit.table.popup_checkbox')}
                  sx={{
                    transform: 'scale(0.8)', // 크기 축소
                    padding: '2px' // 내부 패딩 조정
                  }}
                />
                <Box display="flex" gap={2}>
                  <DatePicker
                    disabled={!isMainPage}
                    format="YYYY-MM-DD"
                    value={endDate}
                    onChange={(newValue: any) => setEndDate(newValue)}
                    minDate={dayjs()}
                    slotProps={{
                      calendarHeader: {
                        format: 'YYYY-MM'
                      },
                      textField: { placeholder: t('notice.edit.table.date_placeholder') }
                    }}
                    sx={{
                      flexGrow: 1,
                      width: '140px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem' // 아이콘 크기 조정
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* 본문 */}
            <Box display="flex" flexDirection="row" alignItems="center">
              <Box boxShadow={1} borderRadius={1} bgcolor={theme.palette.tiptap.main} sx={{ width: '100%' }}>
                <Tiptap ref={editorRef} content={content} editable={true} minHeight="300px" maxHeight="400px" />
              </Box>
            </Box>
          </Box>

          {/* 저장 버튼 */}
          <Box display="flex" justifyContent="flex-start">
            <Button variant="contained" color="primary" onClick={handleSubmit} sx={{ width: '100px', borderRadius: '4px' }}>
              {t('notice.edit.save')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
